import { useState } from "react";
import { CHECKLIST_ITEMS, STATUS_CYCLE, CHIP_STYLES } from "../../constants/data";
import { IconClose, IconChevron } from "../icons/Icons";
import VehicleTrackerModal from "./FahrzeugTrackerModal";

const inputStyle = {
  width: "100%", padding: "6px 10px", border: "1px solid #dde3e8",
  borderRadius: 5, fontSize: 12, color: "#333", background: "#fafafa",
  outline: "none", boxSizing: "border-box",
};
const labelStyle = { fontSize: 11, fontWeight: 600, color: "#555", marginBottom: 3, display: "block" };

const SECTIONS = [
  { key: "vehicle",   label: "Vehicle Information",  dotColor: "#5b9bd5" },
  { key: "project",   label: "Project Information",  dotColor: "#70ad47" },
  { key: "checklist", label: "Checklist",            dotColor: "#ffc000" },
];

export default function VehicleDetailModal({ vehicle, onClose }) {
  const [openSection, setOpenSection] = useState("vehicle");
  const [checklistStatus, setChecklistStatus] = useState(
    Object.fromEntries(CHECKLIST_ITEMS.map(k => [k, "green"]))
  );
  const [trackerOpen, setTrackerOpen] = useState(false);

  function toggleSection(key) {
    setOpenSection(prev => (prev === key ? null : key));
  }

  function setItemStatus(item, status) {
    setChecklistStatus(prev => ({ ...prev, [item]: status }));
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)" }} />

      {/* Modal box */}
      <div style={{
        position: "relative", background: "white", borderRadius: 10,
        width: 520, maxWidth: "95vw", maxHeight: "88vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 8px 40px rgba(0,0,0,0.22)", overflow: "hidden",
        animation: "fadeUp 0.2s ease",
      }}>
        <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }`}</style>

        {/* ── Modal header ── */}
        <div style={{ background: "#1e6b6b", color: "white", padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              {vehicle.vehicleNumber || vehicle.vehicleName || "Vehicle"}
            </div>
            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>
              {vehicle.vehicleName || ""} · {vehicle.hgStatus} · {vehicle.assemblyLine === "chassis" ? "Chassis" : vehicle.assemblyLine === "cab" ? "Cab Only" : ""}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Tracker button */}
            <button
              onClick={() => setTrackerOpen(true)}
              title="Open Vehicle Tracker"
              style={{
                background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.4)",
                borderRadius: 6, cursor: "pointer", color: "white",
                display: "flex", alignItems: "center", gap: 5,
                padding: "5px 10px", fontSize: 11, fontWeight: 600,
                transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.28)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
            >
              Tracker
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <line x1={5} y1={12} x2={19} y2={12}/>
                <polyline points="13 6 19 12 13 18"/>
              </svg>
            </button>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "white", display: "flex", padding: 2 }}>
              <IconClose />
            </button>
          </div>
        </div>

        {/* ── Accordion sections ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
          {SECTIONS.map(sec => (
            <div key={sec.key} style={{ border: "1px solid #dde3e8", borderRadius: 7, overflow: "hidden" }}>
              {/* Section header */}
              <div
                onClick={() => toggleSection(sec.key)}
                style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "9px 14px",
                  background: openSection === sec.key ? "#f0f8f8" : "white",
                  cursor: "pointer", userSelect: "none",
                  borderBottom: openSection === sec.key ? "1px solid #dde3e8" : "none",
                }}
              >
                <span style={{ width: 11, height: 11, borderRadius: "50%", background: sec.dotColor, flexShrink: 0, border: "1.5px solid rgba(0,0,0,0.12)" }} />
                <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: "#1e6b6b" }}>{sec.label}</span>
                <IconChevron open={openSection === sec.key} />
              </div>

              {/* Section body */}
              {openSection === sec.key && (
                <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12, background: "white" }}>

                  {/* ── Vehicle Information ── */}
                  {sec.key === "vehicle" && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 14px" }}>
                      <div>
                        <label style={labelStyle}>Vehicle Number</label>
                        <input style={inputStyle} defaultValue={vehicle.vehicleNumber} readOnly />
                      </div>
                      <div>
                        <label style={labelStyle}>Vehicle Name</label>
                        <input style={inputStyle} defaultValue={vehicle.vehicleName} readOnly />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={labelStyle}>Responsible Person</label>
                        <input style={inputStyle} defaultValue={vehicle.vehicleResponsible} readOnly />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={labelStyle}>Desired Line-Off Date (Customer)</label>
                        <input type="date" style={inputStyle} defaultValue={vehicle.desiredDate} readOnly />
                      </div>
                      <div>
                        <label style={labelStyle}>Line-Off Date VSI (Assigned by VSC)</label>
                        <input type="date" style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Planned Lead Time</label>
                        <input style={inputStyle} placeholder="e.g. 12 weeks" />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={labelStyle}>Cab Line-Off Date</label>
                        <input type="date" style={inputStyle} />
                      </div>
                    </div>
                  )}

                  {/* ── Project Information ── */}
                  {sec.key === "project" && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 14px" }}>
                      <div>
                        <label style={labelStyle}>Project Name</label>
                        <input style={inputStyle} defaultValue={vehicle.projectName} readOnly />
                      </div>
                      <div>
                        <label style={labelStyle}>Project Number</label>
                        <input style={inputStyle} defaultValue={vehicle.projectNumber} readOnly />
                      </div>
                    </div>
                  )}

                  {/* ── Checklist ── */}
                  {sec.key === "checklist" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {/* Column headers */}
                      <div style={{ display: "grid", gridTemplateColumns: "16px 1fr 56px", gap: "0 10px", padding: "4px 6px", borderBottom: "1px solid #eee", marginBottom: 2 }}>
                        <span />
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#888" }}>Item</span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#888", textAlign: "center" }}>Status</span>
                      </div>
                      {CHECKLIST_ITEMS.map(item => (
                        <div key={item} style={{ display: "grid", gridTemplateColumns: "16px 1fr 56px", alignItems: "center", gap: "0 10px", padding: "5px 6px", borderRadius: 4, background: "#fafafa", border: "1px solid #f0f0f0" }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#1e6b6b", display: "block" }} />
                          <span style={{ fontSize: 11, color: "#333" }}>{item}</span>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                            {STATUS_CYCLE.map(c => (
                              <div
                                key={c}
                                onClick={() => setItemStatus(item, c)}
                                title={c}
                                style={{
                                  width: 10, height: 10, borderRadius: "50%", cursor: "pointer",
                                  background: c === "green" ? "#28a745" : c === "yellow" ? "#ffc107" : "#dc3545",
                                  border: checklistStatus[item] === c ? "2px solid #333" : "2px solid transparent",
                                  transition: "border 0.15s",
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Vehicle Tracker modal (renders on top, z-index 400) ── */}
      {trackerOpen && (
        <VehicleTrackerModal
          vehicle={vehicle}
          onClose={() => setTrackerOpen(false)}
        />
      )}
    </div>
  );
}
